import type { Persona, StudioId } from '@/domain/types';
import type { ActiveStudio } from '@/domain/studioScope';
import { STUDIO_IDS } from '@/data/studios';

// Derived from the studio map so the network-wide persona coverage can never
// drift from the real 11 studios.
export const ALL_STUDIOS: StudioId[] = STUDIO_IDS;

export interface PersonaCardSpec {
  persona: Persona;
  name: string;
  role: string;
  description: string;
  defaultHome: string;
  destination: string;
  agentId?: string;
  artistId?: string;
  apprenticeId?: string;
  assignedStudios?: StudioId[];
  activeStudio?: ActiveStudio;
  // Hidden personas keep all their wiring (types, routes, permissions) but are
  // filtered out of the persona selector/switcher — a reversible "turn it off"
  // that doesn't delete the role. Flip to re-enable.
  hidden?: boolean;
}

export const PERSONA_CARDS: PersonaCardSpec[] = [
  {
    persona: 'georgina',
    name: 'Georgina Gray',
    role: 'Operations Manager',
    description:
      'Network-wide accountability across all 11 studios. Works the worklist to zero each day: approvals, cross-studio routing, escalations. The only role that can override a no-deposit booking or cancel one.',
    defaultHome: 'Ops worklist',
    destination: '/ops',
    agentId: 'georgina',
    assignedStudios: ALL_STUDIOS,
    // ops-0: a network role opens on All studios (its whole remit); the
    // switcher narrows to one studio.
    activeStudio: 'all',
  },
  {
    persona: 'messenger',
    name: 'Mara Quinn',
    role: 'Overseas Messenger',
    description:
      "Offshore front line on the Instagram and Facebook inboxes, 5am to 1am AEST. Replies to DMs, pulls the contact number and reference image, and hands phone-numbered leads to the studio's bookings agent.",
    defaultHome: 'Conversations',
    destination: '/conversations',
    assignedStudios: ALL_STUDIOS,
    // ops-0: Mara works every studio's inbox but stays on Coburg by default
    // (flagged for Anas); the switcher widens to 'all'.
    activeStudio: 'coburg',
  },
  {
    persona: 'studio-manager',
    name: 'Eli Marchetti',
    role: 'Studio Manager',
    description:
      'Runs day-of operations at a single studio: the floor, checkouts and payouts, the calendar, roster and front desk. Confirms bookings and resolves issues on site.',
    defaultHome: 'Floor',
    destination: '/floor',
    agentId: 'mgr-coburg',
    assignedStudios: ALL_STUDIOS, // for now: every role gets all studios (was ['coburg'])
    activeStudio: 'coburg',
  },
  {
    persona: 'bookings-agent',
    name: 'Cameron Wells',
    role: 'Bookings Agent',
    description:
      'Works one studio’s prioritised queue. One-click dial from inside the record, disposition at hang-up, and Stripe deposit links sent mid-conversation.',
    defaultHome: 'My Queue',
    destination: '/queue',
    agentId: 'agent-cameron',
    assignedStudios: ALL_STUDIOS, // for now: every role gets all studios (was ['coburg'])
    activeStudio: 'coburg',
  },
  {
    persona: 'artist',
    name: 'Pat Donnelly',
    role: 'Artist',
    description:
      'A read-mostly login: today’s chair sessions with briefs and reference photos, the week ahead, and personal earnings. Deliberate writes only, like in-chair rebooking and post-session rapport notes.',
    defaultHome: 'Artist Dashboard',
    destination: '/artist/pat',
    artistId: 'pat',
    assignedStudios: ALL_STUDIOS, // for now: every role gets all studios (was ['coburg'])
    activeStudio: 'coburg',
  },
  {
    persona: 'apprentice',
    name: 'Tia Bennett',
    role: 'Apprentice',
    description:
      'A learning artist scoped to one studio: their own sessions, plus front-desk capture of walk-ins and in-person deposits. Complaints escalate to the manager.',
    // artists-7: an apprentice is an artist in training, so Tia lands on the same
    // Artist Dashboard as Pat (her own sessions, week, self-book link), keyed by
    // her artist record. The old Apprentice Dashboard route stays for direct links.
    defaultHome: 'Artist Dashboard',
    destination: '/artist/apprentice-tia',
    artistId: 'apprentice-tia',
    apprenticeId: 'apprentice-tia',
    assignedStudios: ALL_STUDIOS, // for now: every role gets all studios (was ['coburg'])
    activeStudio: 'coburg',
  },
  {
    persona: 'owner',
    name: 'Jake Boyer',
    role: 'Owner',
    description:
      'Strategic view: per-studio P&L, network-wide metrics, and cross-studio cost benchmarks, with veto rights on the big calls.',
    defaultHome: 'Owner Dashboard',
    destination: '/owner',
    agentId: 'owner',
    assignedStudios: ALL_STUDIOS,
    // ops-0: a network role opens on All studios (its whole remit); the
    // switcher narrows to one studio.
    activeStudio: 'all',
  },
  // 75n6: the Ops sub-role seats (diagrams 06, 08, 07), each with a seeded
  // person behind it and its own landing screen. Seats only: W9 role
  // permissions stay parked, so these reach their screen, not new powers.
  {
    persona: 'confirmations',
    name: 'Rafael Ortiz',
    role: 'Confirmations',
    description:
      'Works the confirmation sequences across the network: the calls and texts due today, the 2-day reminders, and the reds that go to Ops when a client never answers.',
    defaultHome: 'Confirmations',
    destination: '/confirmations',
    agentId: 'conf-rafael',
    assignedStudios: ALL_STUDIOS,
    activeStudio: 'coburg',
  },
  {
    persona: 'sweeper',
    name: 'Tama Kemp',
    role: 'Sweeper',
    description:
      "Fills this week's gaps in every shop, one artist at a time: bring-forwards, deposits on file, unfinished pieces. Confirms on the call and sends the dated link.",
    defaultHome: 'Sweeper',
    destination: '/sweeper',
    agentId: 'sweep-tama',
    assignedStudios: ALL_STUDIOS,
    activeStudio: 'coburg',
  },
  {
    persona: 'floor',
    name: 'Noor Haddad',
    role: 'Floor Manager',
    description:
      "Runs the studio floor on the day: arrivals, check-ins, the session end and checkout. Sees each sale in front of them, never the day's totals.",
    defaultHome: 'Floor',
    destination: '/floor',
    agentId: 'floor-noor',
    assignedStudios: ALL_STUDIOS, // for now: every role gets all studios
    activeStudio: 'coburg',
  },
  // The two back-office finance seats (diagram 11). Both land on the money-loop
  // (reconciliation) screen. The route/nav gating that lets these seats reach it
  // and hides the non-finance surfaces is wired separately by tv-1xc.
  {
    persona: 'finance',
    name: 'Robin Ashby',
    role: 'Finance / Accounts',
    description:
      "Runs the back-office money loop: reconcile the day's takings and the three-way deposit match, resolving the few lines that didn't marry up.",
    defaultHome: 'Reconciliation',
    destination: '/reconciliation',
    assignedStudios: ALL_STUDIOS,
    // Finance seats span the network but land on the per-studio reconciliation
    // screen, so they start on a concrete studio (not 'all', which that screen
    // can't aggregate); the switcher moves them between studios.
    activeStudio: 'coburg',
  },
  {
    persona: 'finance-manager',
    name: 'Dana Whitlock',
    role: 'Finance Manager',
    description:
      'The second signer on the money loop. Maker-checker sign-off on the unmatched money Accounts has resolved.',
    defaultHome: 'Reconciliation',
    destination: '/reconciliation',
    assignedStudios: ALL_STUDIOS,
    // See finance seat above — per-studio landing screen, so a concrete default.
    activeStudio: 'coburg',
  },
];

export function findPersonaCard(persona: Persona): PersonaCardSpec | undefined {
  return PERSONA_CARDS.find((c) => c.persona === persona);
}
